import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { WindowItem } from '@/hooks/useWindows'
import { cn } from '@/lib/utils'
import { MIN_WINDOW_WIDTH, TASKBAR_HEIGHT } from '@/shared/ui'

type SnapDividerProps = {
  leftWindow: WindowItem
  dividerZIndex: number
  onResize: (leftWidth: number, rightWidth: number) => void
}

export function SnapDivider({ leftWindow, dividerZIndex, onResize }: SnapDividerProps) {
  const { t } = useTranslation()
  const [dragging, setDragging] = useState(false)
  const lastLeftWidthRef = useRef<number>(leftWindow.position.x + leftWindow.size.w)

  useEffect(() => {
    lastLeftWidthRef.current = leftWindow.position.x + leftWindow.size.w
  }, [leftWindow.position.x, leftWindow.size.w])

  const updateWidths = (clientX: number) => {
    if (typeof window === 'undefined') return
    const totalWidth = window.innerWidth
    if (totalWidth <= 0) return
    const minX = MIN_WINDOW_WIDTH
    const maxX = Math.max(minX, totalWidth - MIN_WINDOW_WIDTH)
    const clamped = Math.min(Math.max(clientX, minX), maxX)
    const nextLeftWidth = Math.round(clamped)
    if (nextLeftWidth === lastLeftWidthRef.current) return
    lastLeftWidthRef.current = nextLeftWidth
    const nextRightWidth = Math.max(MIN_WINDOW_WIDTH, totalWidth - nextLeftWidth)
    onResize(nextLeftWidth, nextRightWidth)
  }

  useEffect(() => {
    if (!dragging) return
    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault()
      updateWidths(event.clientX)
    }
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 0) return
      const touch = event.touches[0]
      updateWidths(touch.clientX)
    }
    const stopDragging = () => {
      setDragging(false)
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', stopDragging)
    window.addEventListener('touchmove', handleTouchMove)
    window.addEventListener('touchend', stopDragging)
    window.addEventListener('touchcancel', stopDragging)
    const previousCursor = document.body.style.cursor
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', stopDragging)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', stopDragging)
      window.removeEventListener('touchcancel', stopDragging)
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = ''
    }
  }, [dragging])

  if (typeof document === 'undefined') return null

  const dividerX = leftWindow.position.x + leftWindow.size.w

  const startDragging = (clientX: number) => {
    updateWidths(clientX)
    setDragging(true)
  }

  return createPortal(
    <div
      className={cn('snap-divider', dragging && 'snap-divider--dragging')}
      style={{
        left: dividerX,
        top: 0,
        height: `calc(100vh - ${TASKBAR_HEIGHT}px)`,
        zIndex: dividerZIndex,
      }}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('window.resizeSnapped')}
      onMouseDown={(event) => {
        event.preventDefault()
        startDragging(event.clientX)
      }}
      onTouchStart={(event) => {
        event.preventDefault()
        if (event.touches.length === 0) return
        const touch = event.touches[0]
        startDragging(touch.clientX)
      }}
    >
      <div className="snap-divider__handle" />
    </div>,
    document.body,
  )
}
